import React from 'react'
import {
  useMidiMappingStore,
  formatBindingLabel,
} from '@/stores/midiMappingStore'
import { useMidiStore } from '@/stores/midiStore'

/**
 * Panel section listing every MIDI mapping the user has configured.
 * Rows group by control id. Each row lists the control's label (if
 * the control is currently mounted; fall back to the raw id), each
 * binding as a removable chip, and a clear-all button per row.
 *
 * The Learn-mode toggle lives here too — enabling it pops up the
 * full-screen MidiLearnOverlay so the user can see which on-screen
 * controls are mappable. Forwarding toggle is mirrored for
 * convenience (users usually want both on to test mappings).
 */
export const MappingList: React.FC = () => {
  const bindings = useMidiMappingStore(s => s.bindings)
  const descriptors = useMidiMappingStore(s => s.descriptors)
  const learnMode = useMidiMappingStore(s => s.learnMode)
  const setLearnMode = useMidiMappingStore(s => s.setLearnMode)
  const removeBinding = useMidiMappingStore(s => s.removeBinding)
  const clearBindingsFor = useMidiMappingStore(s => s.clearBindingsFor)
  const clearAllBindings = useMidiMappingStore(s => s.clearAllBindings)

  const forwarding = useMidiStore(s => s.midiForwardingEnabled)
  const setForwarding = useMidiStore(s => s.setMidiForwardingEnabled)

  const entries = Object.entries(bindings).sort(([a], [b]) =>
    a.localeCompare(b),
  )
  const hasAny = entries.length > 0

  return (
    <section className="bg-surface-raised border border-brd-subtle rounded-xl p-3 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">
            Control mappings
          </h4>
          <p className="text-[10px] text-content-tertiary mt-0.5">
            Bind any MIDI message to any mappable UI control. Hold{' '}
            <kbd className="font-mono text-[9px] px-1 py-0.5 rounded border border-brd-subtle bg-surface-sunken">
              Shift+M
            </kbd>{' '}
            from any page to reveal current bindings.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToggleButton
            active={forwarding}
            onClick={() => setForwarding(!forwarding)}
            title="Forward incoming MIDI to mapped controls on every page"
          >
            Forward {forwarding ? 'ON' : 'OFF'}
          </ToggleButton>
          <ToggleButton
            active={learnMode}
            onClick={() => setLearnMode(!learnMode)}
            title="Click a mappable control, then move a knob or press a key to bind"
          >
            {learnMode ? 'Learning…' : 'Learn'}
          </ToggleButton>
          {hasAny && (
            <button
              type="button"
              onClick={clearAllBindings}
              className="text-[10px] font-mono text-content-tertiary hover:text-destructive"
              title="Clear every mapping"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {!hasAny ? (
        <p className="text-[11px] text-content-tertiary italic">
          No mappings yet. Toggle Learn, click a control (Play, BPM, a pad, any
          FX button), then touch your controller.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {entries.map(([controlId, controlBindings]) => {
            const descriptor = descriptors[controlId]
            const label = descriptor?.label ?? controlId
            const mounted = Boolean(descriptor)
            return (
              <li
                key={controlId}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-brd-subtle bg-surface-sunken text-[11px] font-mono"
              >
                <span
                  className={`flex-1 min-w-0 truncate ${
                    mounted ? 'text-content-primary' : 'text-content-tertiary'
                  }`}
                  title={
                    mounted
                      ? controlId
                      : `${controlId} — control not currently mounted; mapping will reattach when the page/component is visible`
                  }
                >
                  {label}
                  {!mounted && (
                    <span className="ml-1 text-[9px] text-content-tertiary">
                      (hidden)
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-1 flex-wrap">
                  {controlBindings.map(binding => (
                    <button
                      key={binding.id}
                      type="button"
                      onClick={() => removeBinding(controlId, binding.id)}
                      className="px-1.5 py-0.5 rounded border border-brd-subtle bg-surface-raised text-[10px] text-content-secondary hover:border-destructive hover:text-destructive transition-colors"
                      title="Click to remove this binding"
                    >
                      {formatBindingLabel(binding)}
                      <span className="ml-1 opacity-60">✕</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => clearBindingsFor(controlId)}
                  className="text-[10px] text-content-tertiary hover:text-destructive"
                  title={`Clear all bindings for ${label}`}
                  aria-label={`Clear all bindings for ${label}`}
                >
                  Clear
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

const ToggleButton: React.FC<{
  active: boolean
  onClick: () => void
  title?: string
  children: React.ReactNode
}> = ({ active, onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`px-2 py-1 rounded border text-[10px] font-semibold uppercase tracking-wider transition-colors ${
      active
        ? 'border-accent bg-accent text-[color:var(--accent-ink)]'
        : 'border-brd-subtle text-content-secondary hover:border-[color:var(--accent-halo)] hover:text-accent'
    }`}
  >
    {children}
  </button>
)
